import { z } from 'zod';
import { uuidSchema } from './comum.ts';

/** Papéis de `.spec/010`. */
export const papelSchema = z.enum([
  'ADMIN',
  'CONTRATOS',
  'PAGAMENTO',
  'RECEBIMENTO',
  'FINANCEIRO',
  'COMPRAS',
  // Entrou em 23/10/2026 (`.spec/015`, decisão 108), logo depois de Compras:
  // é quem autoriza o que Compras lança.
  'AUTORIZADOR_COMPRAS',
  'CONTROLADORIA',
  // Entrou em 07/10/2026 (`.spec/015`, decisão 100). Antes da Gerência, que
  // fecha a lista: é nesta ordem que o cadastro de usuário mostra as caixas, e
  // "leitura ampla" se lê melhor depois dos papéis que lançam.
  'FATURAMENTO',
  'GERENCIA',
]);

export type Papel = z.infer<typeof papelSchema>;

/**
 * Senha: mínimo de 8 caracteres, sem regra de composição.
 *
 * Exigir "1 maiúscula + 1 símbolo" produz `Senha@2026`, que é pior do que uma
 * frase longa — e é contraindicado pelo NIST SP 800-63B (.spec/005, seção 2).
 */
export const senhaSchema = z.string().min(8, 'A senha deve ter ao menos 8 caracteres.').max(128);

/** Código TOTP de seis dígitos. */
export const codigoTotpSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'O código tem 6 dígitos.');

/**
 * Código de recuperação, no formato em que foi exibido: `xxxxx-xxxxx`.
 *
 * Aceito com ou sem o hífen e sem diferenciar maiúsculas — a pessoa que está
 * sem o celular já tem problema suficiente.
 */
export const codigoRecuperacaoSchema = z
  .string()
  .trim()
  .transform((v) => v.toLowerCase().replace(/[^a-z0-9]/g, ''))
  .pipe(z.string().length(10, 'O código de recuperação tem 10 caracteres.'));

export const loginSchema = z.object({
  email: z.string().email(),
  senha: z.string().min(1),
  /**
   * Segundo fator: o código de 6 dígitos do aplicativo, ou um código de
   * recuperação (`xxxxx-xxxxx`). O serviço decide qual é pelo formato.
   */
  totp: z.string().trim().min(6).max(20).optional(),
});

export const loginRespostaSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int(),
  usuario: z.object({
    id: uuidSchema,
    nome: z.string(),
    email: z.string().email(),
    papeis: z.array(papelSchema),
    empresas: z.array(uuidSchema),
    mfaAtivo: z.boolean(),
    /**
     * O papel exige segundo fator e ele ainda não foi ativado.
     *
     * Verdadeiro só para ADMIN sem MFA (`.spec/005`, seção 2). O login é
     * aceito — recusá-lo deixaria a conta nova sem ninguém para ativar — e a
     * tela leva a pessoa direto à ativação.
     */
    mfaPendente: z.boolean().prefault(false),
  }),
});

/* -------------------------------------------------------------------------- */
/* Senha — convite, esqueci, redefinição e alteração                          */
/* -------------------------------------------------------------------------- */

/** "Esqueci minha senha": só o e-mail. A resposta é a mesma exista ele ou não. */
export const esqueciSenhaSchema = z.object({
  email: z.string().trim().toLowerCase().email('Informe um e-mail válido.'),
});
export type EsqueciSenha = z.infer<typeof esqueciSenhaSchema>;

/** O token opaco do link — 32 bytes em base64url dão 43 caracteres. */
export const tokenDeSenhaSchema = z.string().trim().min(40).max(64);

export const finalidadeTokenSchema = z.enum(['CONVITE', 'REDEFINICAO']);
export type FinalidadeToken = z.infer<typeof finalidadeTokenSchema>;

/** O que a tela de definição de senha mostra antes de a pessoa digitar. */
export const tokenDeSenhaInfoSchema = z.object({
  finalidade: finalidadeTokenSchema,
  nome: z.string(),
  email: z.string().email(),
});
export type TokenDeSenhaInfo = z.infer<typeof tokenDeSenhaInfoSchema>;

/** Definir a senha pelo link (convite ou redefinição). */
export const definirSenhaPorTokenSchema = z.object({
  token: tokenDeSenhaSchema,
  senha: senhaSchema,
});
export type DefinirSenhaPorToken = z.infer<typeof definirSenhaPorTokenSchema>;

/** A própria pessoa troca a senha, provando a atual. */
export const alterarSenhaSchema = z
  .object({
    senhaAtual: z.string().min(1, 'Informe a senha atual.'),
    senhaNova: senhaSchema,
  })
  .refine((d) => d.senhaAtual !== d.senhaNova, {
    path: ['senhaNova'],
    message: 'A senha nova precisa ser diferente da atual.',
  });
export type AlterarSenha = z.infer<typeof alterarSenhaSchema>;

/* -------------------------------------------------------------------------- */
/* Segundo fator (TOTP)                                                       */
/* -------------------------------------------------------------------------- */

/** O que a tela de ativação recebe para montar o QR. */
export const mfaIniciarRespostaSchema = z.object({
  /** Segredo em base32, para quem digita à mão no aplicativo. */
  segredo: z.string(),
  /** URL `otpauth://` — é o conteúdo do QR code. */
  otpauth: z.string(),
});
export type MfaIniciarResposta = z.infer<typeof mfaIniciarRespostaSchema>;

export const mfaAtivarSchema = z.object({ codigo: codigoTotpSchema });
export type MfaAtivar = z.infer<typeof mfaAtivarSchema>;

/** Os códigos de recuperação, exibidos uma vez só. */
export const mfaAtivarRespostaSchema = z.object({
  codigosRecuperacao: z.array(z.string()).length(10),
});
export type MfaAtivarResposta = z.infer<typeof mfaAtivarRespostaSchema>;

/** Desativar exige senha e um código válido: quem só roubou a sessão não consegue. */
export const mfaDesativarSchema = z.object({
  senha: z.string().min(1, 'Informe a senha.'),
  codigo: z.string().trim().min(6).max(20),
});
export type MfaDesativar = z.infer<typeof mfaDesativarSchema>;

/** Situação do segundo fator do próprio usuário. */
export const mfaSituacaoSchema = z.object({
  ativo: z.boolean(),
  codigosRestantes: z.number().int().min(0),
  exigidoPeloPapel: z.boolean(),
});
export type MfaSituacao = z.infer<typeof mfaSituacaoSchema>;

/**
 * Uma sessão aberta do próprio usuário — uma família de refresh tokens.
 *
 * `id` é a família, não um token: o token muda a cada renovação, a família
 * dura o login inteiro. `atual` marca a sessão de onde a pergunta veio.
 */
export const sessaoAtivaSchema = z.object({
  id: z.string().uuid(),
  origem: z.enum(['WEB', 'MOBILE']),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  iniciadaEm: z.string().datetime(),
  ultimaRenovacaoEm: z.string().datetime(),
  expiraEm: z.string().datetime(),
  atual: z.boolean(),
});
export type SessaoAtiva = z.infer<typeof sessaoAtivaSchema>;

export const sessoesRespostaSchema = z.object({ sessoes: z.array(sessaoAtivaSchema) });
export type SessoesResposta = z.infer<typeof sessoesRespostaSchema>;

export const sessoesEncerradasSchema = z.object({ encerradas: z.number().int().min(0) });
export type SessoesEncerradas = z.infer<typeof sessoesEncerradasSchema>;

export type LoginResposta = z.infer<typeof loginRespostaSchema>;

/**
 * Tipo do token.
 *
 * Existem dois planos de acesso no sistema — o do cliente e o do console de
 * operação — e os dois são assinados com a mesma chave. O `typ` é o que impede
 * que um token de um plano seja aceito no outro: sem ele, bastaria um token de
 * operador válido para passar pela verificação de uma rota de tenant, e a
 * checagem seguinte olharia só as permissões.
 *
 * O claim é **obrigatório**. Token sem `typ` é recusado, e não tratado como se
 * fosse de tenant por omissão.
 */
export const tipoTokenSchema = z.enum(['TENANT', 'OPERADOR']);
export type TipoToken = z.infer<typeof tipoTokenSchema>;

/** Claims do access token de usuário de conta (.spec/001, seção 4). */
export const jwtClaimsSchema = z.object({
  typ: z.literal('TENANT'),
  sub: uuidSchema,
  tid: uuidSchema,
  roles: z.array(papelSchema),
  perms: z.array(z.string()),
  /**
   * Empresas do grupo às quais o usuário está vinculado (`usuarios_empresas`).
   *
   * É o recorte dos catálogos: empresa, obra, depósito e insumo só aparecem nas
   * listagens quando pertencem a uma destas. **Lista vazia significa nenhuma** —
   * ver `PERMISSAO_IRRESTRITA` para quem escapa.
   *
   * Viaja no token pelo mesmo motivo que `perms`: resolvê-la a cada requisição
   * custaria uma consulta por chamada, e ela só muda quando o vínculo muda —
   * momento em que a sessão é reemitida de qualquer forma.
   */
  emps: z.array(uuidSchema),
  jti: z.string(),
  exp: z.number().int(),
  iat: z.number().int(),
});

export type JwtClaims = z.infer<typeof jwtClaimsSchema>;

/**
 * Claims do access token de operador.
 *
 * **Não tem `tid`**, e isso é estrutural, não omissão: o operador não pertence
 * a conta nenhuma, e a ausência do claim faz qualquer código que tente abrir
 * um contexto de tenant a partir dele falhar na compilação, em vez de abrir um
 * contexto vazio em produção.
 */
export const jwtClaimsOperadorSchema = z.object({
  typ: z.literal('OPERADOR'),
  sub: uuidSchema,
  perfil: z.enum(['OPERACAO', 'SUPORTE']),
  perms: z.array(z.string()),
  jti: z.string(),
  exp: z.number().int(),
  iat: z.number().int(),
});

export type JwtClaimsOperador = z.infer<typeof jwtClaimsOperadorSchema>;

/**
 * Permissão no formato `modulo.recurso.acao`.
 *
 * A checagem no código é **sempre por permissão**, nunca por papel
 * (.spec/001, seção 5). Isso permite criar papéis customizados por tenant
 * depois sem tocar no código.
 *
 * ## O que `.ver` de um cadastro significa hoje
 *
 * **Abrir a tela**, e nada além disso. A leitura dos cadastros não passa mais
 * por aqui: as rotas de consulta de empresa, obra, insumo, fornecedor, plano de
 * contas e afins estão marcadas `'autenticado'` no plugin de auth, e respondem
 * a qualquer usuário da conta.
 *
 * A regra, em uma frase: **o papel decide o menu e a escrita; a escolha é de
 * todo mundo.** Um seletor existe para escolher, e negar a consulta dele porque
 * o usuário não tem o CRUD daquele cadastro devolve uma lista vazia — que se lê
 * como "não há registro", não como "você não pode ver". Nada acusa erro, porque
 * a recusa acontece na consulta e não no salvamento.
 *
 * O modelo anterior era conceder a cada papel a leitura de cada cadastro que
 * suas telas escolhem. Ele falhava duas vezes: esquecia um cadastro a cada tela
 * nova (foi assim que Compras ficou sem obra e sem conta contábil no cadastro
 * de insumo), e, como o menu é recortado pela mesma permissão, conceder a
 * leitura punha a tela de CRUD no menu de quem só precisava do seletor.
 *
 * **Documento continua por papel.** Solicitação, cotação, pedido, nota,
 * títulos a pagar e a receber e lançamentos não são catálogo de escolha: quem
 * compra não lista o contas a pagar.
 */
export const PERMISSOES_POR_PAPEL: Readonly<Record<Papel, readonly string[]>> = {
  ADMIN: ['*'],
  // O cadastro de cliente acompanha o módulo de contratos pelo mesmo motivo que
  // o de fornecedor acompanha Compras (.spec/001, seção 5): é quem lança o
  // contrato que precisa do cliente dele cadastrado. Sem isto, todo cliente
  // novo viraria pedido ao Financeiro antes de o contrato poder ser digitado.
  //
  // Os quatro cadastros de leitura entraram com o **financeiro do contrato**, e
  // pela mesma lógica de segregação de PAGAMENTO e RECEBIMENTO: o formulário do
  // contrato agora escolhe fornecedor (no contrato de pagamento), empresa do
  // grupo, conta contábil e condição de pagamento, e sem eles os quatro
  // seletores abririam vazios. Só `.ver`: quem lança contrato reconhece a
  // empresa e a conta na lista, não as cria.
  //
  // O **painel de obras** (`contratos.painel_obra.ver`) entra pelo curinga
  // `contratos.*`, sem linha própria, e é o único painel do sistema assim. Nos
  // outros a permissão foi escrita à mão justamente para não sair de um curinga
  // de módulo; aqui o curinga é o caminho certo, porque a obra **é** de
  // Contratos — quem responde pelo canteiro é quem responde pelo contrato dele.
  //
  // O sufixo não é `painel` para deixar espaço a um painel de contratos —
  // medição, vigência, saldo a executar —, que seria outra tela com outra
  // pergunta. Quem fica de fora, Financeiro e Controladoria inclusive, está
  // travado por asserção em `auth.test.ts`.
  CONTRATOS: [
    'contratos.*',
    'cadastros.cliente.*',
    'cadastros.fornecedor.ver',
    'cadastros.empresa.ver',
    'cadastros.conta_contabil.ver',
    'cadastros.condicao_pagamento.ver',
    /*
      Preparar o canteiro da obra para receber material.

      A ação escreve no cadastro do insumo — `insumos_empresa` e
      `insumos_deposito` —, e por isso o namespace é `cadastros.insumo.`. Mas ela
      é acionada da tela da obra, e o canteiro é uma consequência de a obra
      existir: sem esta linha, quem cadastra a obra ficaria dependendo de Compras
      para que o depósito que o próprio sistema criou aceitasse a primeira nota.

      Permissão **própria**, e não `cadastros.insumo.editar`. O menu é recortado
      pela permissão de leitura, mas o argumento aqui é outro: `editar` concede o
      cadastro inteiro do insumo — nome, tipo, NCM, classificação contábil — a
      quem só precisa pendurar o catálogo num depósito. Um verbo por ação é o que
      mantém a concessão do tamanho da necessidade.

      Compras a recebe pelo curinga `cadastros.insumo.*`, e o Administrador por
      `*`. A Gerência tem `*.ver` e fica de fora, corretamente: isto é escrita.

      **A irmã dela, `cadastros.insumo.vincular_empresa`, não aparece em lista
      nenhuma — e isso é a regra funcionando, não esquecimento.** Ela é o mesmo
      vínculo por grupo acionado do cadastro de empresa (CAD·01), e quem abre
      aquela tela para editar já tem `cadastros.empresa.editar`: o Financeiro por
      `cadastros.*` e o Admin por `*`. Compras a alcança por
      `cadastros.insumo.*`. Escrevê-la aqui daria a Contratos o poder de encher o
      catálogo de uma empresa que ele não cadastra.
    */
    'cadastros.insumo.vincular_obra',
    /*
      O catálogo de itens de orçamento é escrito por quem orça.

      O namespace é `contratos.`, e não `cadastros.`, apesar de a tabela ser um
      cadastro e as rotas viverem em `/api/v1/cadastros/itens-orcamento`: o que
      decide o namespace da permissão é **quem mantém a lista**, e quem a mantém é
      o orçamentista, não quem cadastra material. Pô-la em `cadastros.` a daria de
      graça ao Financeiro pelo curinga `cadastros.*`, que é justamente o papel que
      não tem nada a ver com o vocabulário comercial.

      A **leitura** não entra em lista nenhuma, e é a regra funcionando: as rotas
      de leitura do catálogo são `autenticado`, porque papel decide menu e
      escrita, não leitura de catálogo. Quem cadastra insumo precisa ver a lista
      para vincular, e não deve precisar de permissão de orçamento para isso.

      O orçamento em si (`contratos.orcamento.*`) entra pelo curinga
      `contratos.*` da primeira linha, como o contrato e a obra — os três são o
      caminho comercial do mesmo negócio, e é a mesma pessoa que percorre os três.
    */
    'contratos.item_orcamento.criar',
    'contratos.item_orcamento.editar',
    'contratos.item_orcamento.excluir',
    /*
      O **saldo a faturar**, e só ele, do módulo de Faturamento (`.spec/015`,
      decisão 100).

      A nota de serviço saiu de Contratos: até 07/10/2026 ela era
      `contratos.nota_servico.*` e vinha pelo curinga da primeira linha. Com ela
      foi embora a permissão que mostrava o saldo a faturar no cadastro da obra,
      no do contrato, nas duas listagens e no painel de obras — e o Adriano
      definiu que Contratos e Gerência **continuam vendo o número**. Quem
      acompanha a obra precisa saber quanto dela falta faturar; emitir a nota é
      outra mão.

      Escrita à mão, e não `faturamento.*`: é a concessão de uma leitura, do
      tamanho da necessidade. A Gerência a alcança pelo `*.ver`.
    */
    'faturamento.saldo_a_faturar.ver',
  ],
  // `pagamento.*` cobre o título e a baixa dele. Os cadastros que a tela de
  // contas a pagar escolhe — conta corrente, histórico, plano de contas,
  // fornecedor, obra — entram **só de leitura**: quem paga precisa reconhecer o
  // banco e o fornecedor na lista, e não criá-los. É a segregação de `.spec/005`,
  // seção 3, escrita como permissão: cadastrar o fornecedor e pagar a ele são
  // duas mãos diferentes, e é o par que evita o pagamento a um fornecedor que
  // não existia até o momento do pagamento.
  PAGAMENTO: [
    'pagamento.*',
    'cadastros.fornecedor.ver',
    'cadastros.conta_corrente.ver',
    'cadastros.historico_bancario.ver',
    'cadastros.historico_financeiro.ver',
    'cadastros.conta_contabil.ver',
    'contratos.obra.ver',
  ],
  // Espelho de PAGAMENTO, com o cliente no lugar do fornecedor: `recebimento.*`
  // cobre o título e a baixa dele, e os cadastros que a tela escolhe entram
  // **só de leitura**. Quem recebe precisa reconhecer o banco e o cliente na
  // lista, e não criá-los — cadastrar o cliente e dar baixa no que ele deve são
  // duas mãos diferentes, e é o par que evita a baixa em nome de um cliente que
  // não existia até o momento da baixa (`.spec/005`, seção 3).
  //
  // `contratos.obra.ver` entra porque o título é rateado por obra, como no
  // pagar; sem ele o seletor de obra do rateio abriria vazio.
  RECEBIMENTO: [
    'recebimento.*',
    'cadastros.cliente.ver',
    'cadastros.conta_corrente.ver',
    'cadastros.historico_bancario.ver',
    'cadastros.historico_financeiro.ver',
    'cadastros.conta_contabil.ver',
    'contratos.obra.ver',
  ],
  /*
    Os dois painéis de título são as **únicas** permissões de `pagamento.` e
    `recebimento.` que o Financeiro recebe, e a exceção é deliberada.

    Ele não tem `pagamento.*` nem `recebimento.*`, e não deve ter: a `.spec/010`
    separa quem mantém o cadastro financeiro e lança no caixa de quem decide o
    que se paga e de quem cobra — é o par que evita a mesma mão cadastrar o
    fornecedor e pagar a ele, e cadastrar o cliente e dar baixa no que ele deve.
    Abrir os curingas aqui desfaria as duas segregações de uma vez, e daria ao
    Financeiro as baixas junto.

    Os painéis, sozinhos, não desfazem nada: são leitura agregada, sem uma única
    rota de escrita, e o que respondem — quanto sai nesta semana, quanto entra,
    quanto está em atraso dos dois lados — é exatamente a pergunta de quem
    projeta o caixa. As duas pontas na mesma mesa são o fluxo de caixa; sem elas,
    o Financeiro pediria os números a dois setores toda segunda-feira.

    Escritas à mão, e não como `pagamento.painel.*`: o dia em que um painel
    ganhar um segundo recurso, ele nasce fechado até alguém decidir o contrário.
    É a mesma escolha de `compras.painel.ver` em COMPRAS, logo abaixo.

    O **painel financeiro**, que confronta as duas pontas, não aparece aqui: a
    permissão dele é `financeiro.painel.ver`, e o curinga `financeiro.*` já a
    alcança. Ela é de `financeiro.` justamente para não ser de nenhum dos dois
    módulos de título — exigir `pagamento.painel.ver` daria o confronto a quem
    só enxerga a dívida, e com ele a carteira de cobrança inteira.
  */
  FINANCEIRO: ['cadastros.*', 'financeiro.*', 'pagamento.painel.ver', 'recebimento.painel.ver'],
  COMPRAS: [
    'compras.solicitacao.*',
    // A cotação é o trabalho do comprador, não um cadastro de apoio: é ele que
    // liga para os fornecedores e digita o que cada um respondeu. Sem acesso
    // total, registrar a resposta de uma ligação viraria pedido a outro setor.
    'compras.cotacao.*',
    // Inclui `compras.pedido.enviar` (decisão 114): quem fecha o pedido é quem o
    // manda ao fornecedor, e o mesmo papel já baixa o PDF que vai anexado.
    'compras.pedido.*',
    /*
      A lista de compra direta é o caminho curto da compra, e fica com Compras
      pela mesma razão que a solicitação: é ela quem responde pelo que se compra,
      e é o Compras que transforma a lista em pedido.

      Linha própria, e não `compras.*`: o curinga concederia de antemão todo
      recurso futuro do módulo, que é justamente o que as linhas separadas de
      solicitação, cotação e pedido evitam.
    */
    'compras.lista_compra_direta.*',
    /*
      O envio em lote para aprovação (`CMP·09`, decisão 110): a mesa de quem
      lança, irmã da do autorizador. Sob `compras.envio_aprovacao.`, e não sob
      `compras.aprovacao.`, que é do Autorizador de compras inteiro — o curinga
      dele daria a tela de envio a quem não lança nada.
    */
    'compras.envio_aprovacao.*',
    /*
      O painel é a tela com que o comprador abre o dia, e a permissão dele é
      **própria** — `compras.painel.ver` não sai de nenhum dos três curingas
      acima, porque nenhum deles é `compras.*`.

      Escrevê-la aqui, e não alargar a lista para `compras.*`, é a mesma escolha
      que já separa solicitação, cotação e pedido em três linhas: o curinga
      concederia de antemão todo recurso futuro do módulo, e o próximo — uma
      aprovação de solicitação, por exemplo — nasceria liberado ao comprador sem
      ninguém ter decidido isso.

      Administrador (`*`) e Gerência (`*.ver`) já a alcançam pelos curingas
      deles, e por isso não aparecem aqui.
    */
    'compras.painel.ver',
    'cadastros.insumo.*',
    'cadastros.grupo_insumo.*',
    'cadastros.fornecedor.*',
    /*
      O de-para do catálogo do fornecedor, **completo**.

      Compras é quem conhece o catálogo de quem vende: é ela que cota, que negocia
      e que recebe a lista de preços com os códigos dele. Manter o de-para em dia
      é trabalho de comprador, e o namespace é próprio — `cadastros.fornecedor.*`
      não o alcança — porque Controladoria também escreve aqui sem poder ganhar o
      cadastro inteiro do fornecedor. Ver `.spec/015`, decisão 20.
    */
    'cadastros.fornecedor_item.*',
    // A unidade de medida acompanha o insumo, e só de leitura: é no cadastro do
    // insumo que ela se escolhe, mas criar unidade vale para a conta inteira —
    // duas siglas para a mesma coisa partem o estoque em duas linhas. A conta
    // nasce com as vinte e quatro do provisionamento, e é raro faltar.
    'cadastros.unidade_medida.ver',
    // A conversão entre unidades segue a unidade: só leitura, e pelo mesmo
    // motivo. Compras consome a sugestão ao preencher o fator de um item do
    // fornecedor; quem decide que 1 T são 1.000 KG na conta é o Financeiro.
    'cadastros.conversao_unidade.ver',
    // Só leitura: o pedido de compra indica para onde o material vai, e o
    // comprador precisa reconhecer o depósito na lista. Quem cria e desativa
    // local de estoque é quem responde por ele — Controladoria.
    'cadastros.deposito.ver',
    // Também só leitura, e pelo mesmo motivo: o pedido escolhe a condição de
    // pagamento negociada, mas em quantas vezes a empresa aceita comprar é
    // decisão do Financeiro, não do comprador.
    'cadastros.condicao_pagamento.ver',
  ],
  // O cadastro de depósito acompanha `controladoria.estoque.*` pelo mesmo
  // motivo que o fornecedor acompanha Compras: é quem movimenta estoque que
  // sabe no mesmo instante que nasceu um local novo. Sem isto, todo canteiro
  // novo viraria pedido ao Financeiro antes de a primeira entrada ser digitada.
  CONTROLADORIA: [
    'controladoria.nota.*',
    'controladoria.estoque.*',
    /*
      A transferência entre depósitos tem namespace **próprio**, e não sai de
      `controladoria.estoque.*`: lançar perda num galpão e decidir que material
      muda de canteiro são decisões de tamanhos diferentes, e a segunda move saldo
      entre duas obras de uma vez.

      A Controladoria recebe as duas hoje, porque é a mesma mão. A separação
      existe para o dia em que não for: conceder só uma passa a ser cadastro de
      papel, e não mudança de código — a mesma razão de `consumo.*` estar fora de
      `controladoria.*`, logo abaixo.
    */
    'controladoria.transferencia.*',
    'controladoria.mao_obra.*',
    /*
      O consumo é módulo próprio na `.spec/010` — "acesso ao módulo de nota de
      compra" e "acesso ao módulo de consumo" são duas linhas separadas —, e o
      namespace reflete isso: `consumo.*`, e não `controladoria.consumo.*`.

      A Controladoria recebe os dois hoje porque é a mesma mão que confere o que
      chegou e baixa o material para a obra. A separação existe para o dia em que
      não for: conceder só um dos dois passa a ser mudança de cadastro de papel,
      e não de código.
    */
    'consumo.*',
    'cadastros.deposito.*',
    // O tipo de movimentação acompanha o depósito pelo mesmo motivo, e com o
    // mesmo alcance: é quem lança entrada e saída que descobre, no meio do
    // lançamento, que falta o motivo no cadastro. Sem isto, cada perda ou
    // devolução nova viraria pedido ao Financeiro antes de o movimento poder
    // ser digitado.
    'cadastros.tipo_movimentacao_estoque.*',
    // A unidade entra só de leitura, como em Compras: o lançamento de estoque
    // exibe a unidade do insumo ao lado da quantidade — e é ela que decide se a
    // fração é aceita —, mas quem cadastra unidade é o Financeiro.
    'cadastros.unidade_medida.ver',
    // A conversão entre unidades também só de leitura, seguindo a unidade: a
    // conferência do XML consome a sugestão de fator, não a define.
    'cadastros.conversao_unidade.ver',
    /*
      O de-para do catálogo do fornecedor, **completo** — e é a única concessão de
      escrita que a Controladoria recebe num cadastro de Compras.

      A razão é a ordem em que o trabalho acontece: a linha do de-para nasce na
      **conferência da nota vinda de XML** (`.spec/015`, decisões 19 e 20), uma
      por vez, enquanto quem tem o documento na mão casa as linhas do arquivo com
      o catálogo da conta. Sem escrita aqui, cada código novo pararia a
      conferência para virar pedido ao comprador — e o conferente é justamente
      quem sabe, naquele instante, que material chegou.

      Namespace próprio, e não `cadastros.fornecedor.*`: aquele concede o cadastro
      inteiro do fornecedor — documento, endereço, dados bancários — a quem só
      precisa dizer que o código `201.01.028` dele é o cimento daqui. Um verbo por
      ação é o que mantém a concessão do tamanho da necessidade, como na
      `cadastros.insumo.vincular_obra` de Contratos.
    */
    'cadastros.fornecedor_item.*',
    // A nota de compra é onde o parcelamento do fornecedor vira título a pagar,
    // e a condição precisa ser reconhecida na lista. Só leitura: quem define as
    // condições que a empresa pratica é o Financeiro.
    'cadastros.condicao_pagamento.ver',
    // A operação fiscal entra **só de leitura**, ao contrário do tipo de
    // movimentação. Quem digita a nota escolhe a operação item a item e precisa
    // reconhecê-la na lista; mas as flags dela decidem quanto imposto é
    // subtraído do custo de estoque, e criar operação é decisão fiscal, do
    // Financeiro. Uma operação cadastrada às pressas com "credita ICMS" marcado
    // por engano subavalia o custo em silêncio — e custo baixo ninguém procura.
    'cadastros.operacao_fiscal.ver',
    /*
      Os quatro cadastros que a nota de compra **escolhe**, todos só de leitura.

      Quem digita a nota precisa reconhecer na lista a empresa que comprou, o
      fornecedor que emitiu, o insumo de cada linha e a obra de cada fatia de
      rateio — sem eles a tela abre com quatro campos vazios e nenhuma explicação,
      porque a recusa acontece na consulta do seletor, não no salvamento.

      Só leitura, e a diferença em relação ao depósito e ao tipo de movimentação é
      deliberada: aqueles dois nascem no meio do lançamento — o canteiro novo, o
      motivo da perda —, e estes quatro já existem antes de a nota chegar. Quem
      cadastra empresa e fornecedor é o Financeiro; insumo é de Compras; obra é de
      Contratos.
    */
    'cadastros.empresa.ver',
    'cadastros.fornecedor.ver',
    'cadastros.insumo.ver',
    'contratos.obra.ver',
    /*
      O pedido de compra entra **só de leitura**, e é o que sustenta a entrada de
      nota por pedido: a tela lista os pedidos que ainda esperam entrega para que
      a nota nasça deles em vez de ser digitada de novo. Quem compra continua
      sendo Compras — a Controladoria confere o que chegou contra o que foi
      pedido, e não altera o pedido.
    */
    'compras.pedido.ver',
  ],
  GERENCIA: ['gerencia.*', '*.ver'],
  /*
    Quem emite as notas da construtora (`.spec/015`, decisão 100).

    `faturamento.*` é o módulo inteiro — hoje a nota de serviço e o saldo a
    faturar; a nota de produto entra por aqui quando existir. Curinga, e não
    linha por recurso como em Compras: o módulo foi criado para ter um papel só
    dele, e é esse o papel.

    **Cliente e operação fiscal completos**, por decisão do Adriano. A emissão
    cobra do cliente e-mail, inscrição municipal e endereço, e quem fatura é
    quem descobre a falta, com a nota aberta; e a operação fiscal de saída é o
    que decide o ISS, as retenções e a conta de receita da nota. `cadastros.
    operacao_fiscal.*` alcança também as de **entrada**, que são do Financeiro:
    a permissão não separa os dois sentidos, e criar uma segunda só para isso
    pediria uma regra que ninguém pediu.

    **Obra e contrato só de leitura**: a nota aponta os dois, e quem fatura a
    medição abre o contrato para ler o que foi combinado e a obra para ver o
    que ela ainda tem a faturar. Não edita nenhum dos dois — o valor a faturar
    continua sendo de quem responde pela obra.

    **Empresa, serviço, conta contábil e condição de pagamento não têm linha
    aqui**, e é a regra do topo deste mapa funcionando: o papel decide o menu e
    a escrita, a escolha é de todo mundo. A nota escolhe os quatro pelas
    leituras de catálogo, que são `autenticado`. Dar `.ver` de cada um poria
    quatro telas de cadastro no menu de quem só fatura — e a primeira delas,
    Empresas, viraria a tela em que esse usuário cai ao entrar
    (`PRIMEIRO_MODULO`, no roteador). O que a emissão acusa como faltando
    nesses cadastros vem nomeado na pendência, e quem corrige é quem os mantém.

    Nenhum título: quem cobra a nota emitida é o Recebimento. É a mesma
    separação de Contratos, travada em `auth.test.ts`.
  */
  /*
    Quem autoriza a lista de compra direta, a solicitação e o pedido (decisão
    108), quando a conta liga a autorização de cada um.

    **A permissão de aprovar não mora sob o documento.** `compras.pedido.aprovar`
    sairia do curinga `compras.pedido.*` do papel Compras, e o comprador
    aprovaria o próprio pedido sem ninguém ter decidido isso — é o risco que o
    comentário de COMPRAS, acima, já previa. Sob `compras.aprovacao.`, ela não
    sai de curinga nenhum de Compras e, por não terminar em `.ver`, também não
    sai do `*.ver` da Gerência. Uma por documento, para a conta poder ter quem
    autoriza só pedido.

    Os três `.ver` são o que deixa abrir o documento que se vai decidir; a
    cotação, para conferir de onde veio o preço do pedido. Nada de criar ou
    editar: quem autoriza não lança.
  */
  AUTORIZADOR_COMPRAS: [
    'compras.aprovacao.*',
    'compras.lista_compra_direta.ver',
    'compras.solicitacao.ver',
    'compras.cotacao.ver',
    'compras.pedido.ver',
  ],
  FATURAMENTO: [
    'faturamento.*',
    'cadastros.cliente.*',
    'cadastros.operacao_fiscal.*',
    'contratos.obra.ver',
    'contratos.contrato.ver',
  ],
} as const;

/**
 * Quem enxerga o grupo inteiro, sem o recorte de `emps`.
 *
 * O Admin, e só ele: é quem cadastra empresa e quem conserta vínculo errado, e
 * recortá-lo deixaria uma conta nova sem ninguém capaz de abrir o cadastro de
 * empresa para se vincular. Os demais papéis, Gerência inclusive, veem o que o
 * vínculo diz.
 *
 * **O prefixo `escopo.` não é decorativo.** Sob `cadastros.` esta permissão
 * seria concedida por tabela ao Financeiro, que tem `cadastros.*` — e o recorte
 * cairia para ele sem ninguém escrever isso em lugar nenhum. Terminada em algo
 * diferente de `.ver`, ela também escapa do `*.ver` da Gerência. Quem a alcança
 * é o `'*'` do Admin, e mais nada.
 *
 * A checagem continua sendo por permissão, nunca por papel (.spec/001, seção 5):
 * um tenant que amanhã queira um papel de auditoria irrestrito concede esta
 * string, sem tocar no código.
 */
export const PERMISSAO_IRRESTRITA = 'escopo.empresa.irrestrito';

/**
 * As empresas que este portador enxerga, ou `null` quando não há recorte.
 *
 * `null` e `[]` são coisas diferentes, e a distinção é o coração da regra:
 * `null` é "vê tudo", `[]` é "não vê nada". Um `[]` tratado como ausência de
 * filtro abriria o grupo inteiro para quem não foi vinculado a empresa nenhuma
 * — exatamente o contrário do que se queria, e sem nada na tela indicando.
 */
export function escopoDeEmpresas(portador: {
  perms: readonly string[];
  emps: readonly string[];
}): readonly string[] | null {
  if (temPermissao(portador.perms, PERMISSAO_IRRESTRITA)) return null;
  return portador.emps;
}

/** Verifica uma permissão contra os padrões concedidos, com curinga. */
export function temPermissao(concedidas: readonly string[], exigida: string): boolean {
  return concedidas.some((padrao) => {
    if (padrao === '*') return true;
    if (padrao === exigida) return true;

    if (padrao.endsWith('.*')) {
      return exigida.startsWith(padrao.slice(0, -1));
    }
    if (padrao.startsWith('*.')) {
      return exigida.endsWith(padrao.slice(1));
    }
    return false;
  });
}

export function permissoesDosPapeis(papeis: readonly Papel[]): string[] {
  return [...new Set(papeis.flatMap((papel) => PERMISSOES_POR_PAPEL[papel]))];
}
